/**
 * `NavStrip` — the bottom nav. `docs/design-direction.md` §9.2.
 *
 * 44px on `--bg-panel` under the mini player, three items divided by 1px verticals,
 * mono caps: `ЯЩИК` · `ПОИСК` · `МОЁ`. The active item takes a 2px `--accent` top
 * border and the three-tick ruler glyph — the same one-drop motif doing its fourth
 * job (§6.6). Together with the mini player the bottom chrome is
 * `100px + env(safe-area-inset-bottom)`, which every scroll container pads for; the
 * shell (T37) owns that sum, this component owns its 44px.
 *
 * **Queue is still not a tab.** It is a sheet, opened from the transport or by a
 * long-press on the mini player.
 *
 * **There are FOUR items, and this comment used to forbid the fourth.** It said three and
 * that there would never be a fourth; the operator asked for the PADS screen on
 * 2026-09-06 and chose to make it a destination rather than a sheet, and
 * `design-direction.md` §9.2 was amended to match. The strip absorbs it with no CSS
 * change at all — `grid-auto-flow: column` with `grid-auto-columns: 1fr` — and at 320px
 * four cells are 80px each, which `СЕМПЛЫ` fits in the 11px mono tier once `hasCyrillic`
 * drops the tracking. That fit is the real constraint on anything added here: a fifth
 * item, or a longer word than `СЕМПЛЫ`, does not survive 320px at Telegram's largest
 * font setting.
 *
 * The labels are Russian in the 11px mono tier, so each one goes through the
 * `hasCyrillic` guard: +0.16em tracking on Russian caps reads as shouting and is
 * banned (§4.3, §12.16). The tracking drops and the forced uppercase comes off — the
 * copy is authored in caps already, so it still prints as caps.
 *
 * `aria-current="page"` marks the active item, and the ruler glyph is `aria-hidden`
 * because it is the printed form of exactly that state.
 */

import type { JSX } from 'react';

import { hasCyrillic } from '@bl/shared';

import styles from './components.module.css';
import { Ruler } from './primitives/Ruler';
import { cx } from './primitives/Rule';

/** The three destinations. There is no fourth: the queue is a sheet (§9.2). */
export type NavKey = 'crate' | 'search' | 'mine' | 'pads';

export interface NavItem {
  readonly key: NavKey;
  readonly label: string;
}

export const NAV_ITEMS: readonly NavItem[] = Object.freeze([
  Object.freeze({ key: 'crate' as const, label: 'ЯЩИК' }),
  Object.freeze({ key: 'search' as const, label: 'ПОИСК' }),
  Object.freeze({ key: 'mine' as const, label: 'МОЁ' }),
  Object.freeze({ key: 'pads' as const, label: 'СЕМПЛЫ' }),
]);

/** The strip's own accessible name. */
export const NAV_LABEL = 'Разделы';

export interface NavStripProps {
  readonly active: NavKey;
  readonly onSelect: (key: NavKey) => void;
  readonly items?: readonly NavItem[];
  readonly className?: string | undefined;
}

export function NavStrip({
  active,
  onSelect,
  items = NAV_ITEMS,
  className,
}: NavStripProps): JSX.Element {
  return (
    <nav className={cx(styles.nav, className)} aria-label={NAV_LABEL}>
      {items.map((item) => {
        const current = item.key === active;
        return (
          <button
            key={item.key}
            type="button"
            className={cx('t-mono', 't-11', styles.navItem, hasCyrillic(item.label) && styles.cyr)}
            aria-current={current ? 'page' : undefined}
            onClick={() => {
              onSelect(item.key);
            }}
          >
            {item.label}
            {current ? <Ruler variant="nav" /> : null}
          </button>
        );
      })}
    </nav>
  );
}
